'use client';

import { Text } from '../../components/text';
import { HStack, VStack, Box, css } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { FileCodeIcon } from '@terra/ui-icons';
import { MarkdownPreview } from '../';

const filePathStyles = css({
  fontFamily: 'mono',
  fontSize: 'sm',
  color: 'fg.default',
});
const diffContainerStyles = css({
  borderRadius: 'sm',
  overflow: 'hidden',
  '& pre': {
    margin: '0 !important',
    padding: '12px !important',
    borderRadius: 'sm !important',
  },
});

export type RemediationPlan = {
  repositoryName?: string;
  overview?: string;
  changes?: Array<{
    file: string;
    description: string;
    diff: string;
    summary: string;
  }>;
  finalSummary?: string;
  cannotBuildPlan?: boolean;
};
export function parseRemediationPlan(
  planString: string | null | undefined,
): RemediationPlan | null {
  if (!planString) return null;

  try {
    return JSON.parse(planString) as RemediationPlan;
  } catch {
    return null;
  }
}

type RemediationPlanContentProps = {
  plan: RemediationPlan;
};

export function RemediationPlanContent({ plan }: RemediationPlanContentProps) {
  const t = useTranslations('designSystem.findings.remediationPlan');
  const hasChanges = plan.changes && plan.changes.length > 0;

  return (
    <VStack gap={4} alignItems="stretch">
      {plan.overview && (
        <VStack gap={2} alignItems="stretch">
          <Text fontWeight="medium" fontSize="sm">
            {t('overview')}
          </Text>
          <Text fontSize="sm" color="fg.muted">
            {plan.overview}
          </Text>
        </VStack>
      )}

      {hasChanges && (
        <VStack gap={2} alignItems="stretch">
          <Text fontWeight="medium" fontSize="sm">
            {t('changes', { count: plan.changes?.length ?? 0 })}
          </Text>
          <VStack gap={4} alignItems="stretch">
            {plan.changes?.map((change, index) => (
              <VStack key={index} gap={2} alignItems="stretch">
                <HStack gap={2}>
                  <FileCodeIcon size={14} />
                  <Text className={filePathStyles}>{change.file}</Text>
                </HStack>
                <Text fontSize="sm" color="fg.muted">
                  {change.summary}
                </Text>
                <Box className={diffContainerStyles}>
                  <MarkdownPreview
                    source={`\`\`\`diff\n${change.diff}\n\`\`\``}
                    fontSize={12}
                  />
                </Box>
              </VStack>
            ))}
          </VStack>
        </VStack>
      )}

      {plan.finalSummary && (
        <VStack gap={2} alignItems="stretch">
          <Text fontWeight="medium" fontSize="sm">
            {t('summary')}
          </Text>
          <Text fontSize="sm" color="fg.muted">
            {plan.finalSummary}
          </Text>
        </VStack>
      )}
    </VStack>
  );
}
